import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { useOnboardingState } from '../../hooks/useOnboardingState.jsx';
import { onboardingV2Api } from '@/data/api/onboardingV2';
import Logo from '@/Asset/images/logo-blaxk.webp';
import EastIcon from '@mui/icons-material/East';
import Toast from '@/utils/toastManager';
import StepIndicator from '../StepIndicator/StepIndicator';
import './Step5.css';

const Step5 = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { state, updateState } = useOnboardingState();
	const planFromQuery = useMemo(() => new URLSearchParams(location.search).get('plan'), [location.search]);
	const isRetake = useMemo(() => new URLSearchParams(location.search).get('retake') === 'true' || location.state?.isRetake === true, [location.search, location.state?.isRetake]);
	const isStudioPlan = state.selectedPlanType === 'studio' || planFromQuery === 'studio';
	const totalSteps = isStudioPlan ? (isRetake ? 5 : 6) : 4;
	const resourceOptions = useMemo(
		() => [
			{ id: 'structured-lesson-plans', label: 'Structured Lesson Plans', },
			{ id: 'teacher-continued-education', label: 'Teacher Continued Education Videos', },
			{ id: 'warm-up-videos', label: 'Warm Up Videos', },
			{ id: 'across-the-floor-videos', label: 'Across The Floor Videos', },
			{ id: 'injury-reduction-resources', label: 'Injury Reduction Resources / Videos', },
			{ id: 'technique-breakdowns', label: 'Technique Breakdowns', }
		],
		[]
	);
	const [selectedResources, setSelectedResources] = useState([]);

	useEffect(() => {
		if (state.studioResourceNeeds) setSelectedResources(state.studioResourceNeeds);
	}, [state.studioResourceNeeds]);

	// Validation function for StepIndicator - checks if current step is valid
	const validateStep = () => {
		if (selectedResources.length === 0) {
			Toast.error('Please select at least one resource to continue.');
			return false;
		}
		return true;
	};

	// Handle validation failure for StepIndicator
	const handleValidationFail = () => {
		// Toast is already shown in validateStep
	};

	const toggleResource = id => {
		setSelectedResources(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
	};

	const handleNext = async () => {
		// Validate required selections
		if (selectedResources.length === 0) {
			Toast.error('Please select at least one resource to continue.');
			return;
		}

		const onboardingPreferences = {
			...(state.onboardingPreferences || {}),
			studioResourceNeeds: selectedResources
		};

		try {
			await onboardingV2Api.updateOnboardingData({
				onboardingStep: 9,
				onboardingPreferences
			});
		} catch (error) {
			Toast.error('Failed to save this step. Please try again.');
			return;
		}

		updateState({
			currentStep: 9,
			studioResourceNeeds: selectedResources,
			onboardingPreferences
		});

		if (isRetake) {
			navigate(`/onboarding/recommended-videos?${new URLSearchParams(location.search).toString()}`);
			return;
		}

		navigate(`/onboarding/step-6?${new URLSearchParams(location.search).toString()}`);
	};

	const handleSkip = async () => {
		try {
			await onboardingV2Api.updateOnboardingData({
				onboardingStep: 9
			});
		} catch (error) {
			Toast.error('Failed to save this step. Please try again.');
			return;
		}

		updateState({ currentStep: 9 });
		navigate('/');
	};

	return (
		<div className="onboarding-step-five">
			<div className="onboarding-step-five__grid-overlay" />
			<div className="onboarding-step-five__content">
				<header className="onboarding-step-five__header">
					<div className="onboarding-step-five__brand-row">
						<button
							type="button"
							className="onboarding-step-five__back-btn"
							onClick={() => navigate(`/onboarding/step-4?${new URLSearchParams(location.search).toString()}`)}
						>
							<KeyboardBackspaceOutlinedIcon />
						</button>
						<img src={Logo} alt="The Godfrey Method On Demand" className="onboarding-step-five__logo" />
					</div>

					<StepIndicator 
						totalSteps={totalSteps} 
						currentStep={5}
						validateStep={validateStep}
						onValidationFail={handleValidationFail}
						basePath="/onboarding"
					/>
				</header>

				<main className="onboarding-step-five__main">
					<h1 className="onboarding-step-five__title">
						What Resources Would Be Most Helpful To You And Your Instructors?
					</h1>
					<p className="onboarding-step-five__subtitle">Please select all that apply</p>

					<div className="onboarding-step-five__cards">
						{resourceOptions.map(option => {
							const isSelected = selectedResources.includes(option.id);
							return (
								<button
									key={option.id}
									type="button"
									className={`onboarding-step-five__card is-studio ${isSelected ? 'is-selected' : ''}`}
									onClick={() => toggleResource(option.id)}
								>
									<p className="onboarding-step-five__studio-card-label">{option.label}</p>
									<span className={`onboarding-step-five__studio-card-dot ${isSelected ? 'is-selected' : ''}`}>
										{isSelected ? <CheckIcon /> : null}
									</span>
								</button>
							);
						})}
					</div>
				</main>

				<footer className="onboarding-step-five__footer">
					<button 
						type="button" 
						onClick={handleNext} 
						className={`onboarding-step-five__continue-btn ${selectedResources.length === 0 ? 'is-disabled' : ''}`}
						disabled={selectedResources.length === 0}
					>
						Continue <EastIcon/>
					</button>
					<button type="button" onClick={handleSkip} className="onboarding-step-five__skip-btn">
						Skip
					</button>
				</footer>
			</div>
		</div>
	);
};

export default Step5;
